import { Link } from 'react-router-dom';

const tools = [
  {
    title: 'School Shutdown Impact Map',
    description:
      'Interactive map to visualize the impact of DJUSD elementary school closures. Toggle schools on/off, see enrollment redistribution, and analyze travel distance impacts.',
    path: '/djusd-shutdown-impact-map',
    status: 'live' as const,
    attribution: {
      dataSource: 'MGT',
      dataSourceUrl: 'https://www.mgt.us/',
      modelCredit: 'Brian Russell',
      email: 'briancrussell@gmail.com',
    },
  },
];

export function HomePage() {
  return (
    <div className="min-h-[calc(100vh-120px)]">
      {/* Hero */}
      <div className="bg-gradient-to-br from-davis-navy to-davis-navy-light text-white py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-display mb-3">
            Davis, CA Community Tools
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mb-4">
            Open tools and resources for the Davis community.
          </p>
          <a
            href="https://baycrewtech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-davis-gold/15 border border-davis-gold/30 rounded-full px-4 py-2 hover:bg-davis-gold/25 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-davis-gold" fill="currentColor">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M12 2 L14 10 L12 8 L10 10 Z" />
              <path d="M12 22 L10 14 L12 16 L14 14 Z" />
              <path d="M2 12 L10 10 L8 12 L10 14 Z" />
              <path d="M22 12 L14 14 L16 12 L14 10 Z" />
            </svg>
            <span className="text-sm font-display font-bold tracking-wide text-davis-gold">
              Built by Bay Crew Tech
            </span>
            <svg className="w-3.5 h-3.5 text-davis-gold/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-4.5-6h6m0 0v6m0-6L9.75 14.25" />
            </svg>
          </a>
        </div>
      </div>

      {/* Tools grid */}
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <h2 className="text-sm font-semibold text-davis-slate uppercase tracking-wide mb-6">
          Available Tools
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          {tools.map((tool) => (
            <Link
              key={tool.path}
              to={tool.path}
              className="group block bg-white border border-gray-200 rounded-xl p-6 hover:border-davis-blue hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-davis-navy/10 flex items-center justify-center">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-5 h-5 text-davis-navy"
                    fill="currentColor"
                  >
                    <path d="M12 2C9 2 6 5 6 10c0 4 3 8 6 12 3-4 6-8 6-12 0-5-3-8-6-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
                  </svg>
                </div>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    tool.status === 'live'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tool.status === 'live' ? 'Live' : 'Coming Soon'}
                </span>
              </div>
              <h3 className="text-lg font-display text-davis-charcoal group-hover:text-davis-navy transition-colors mb-2">
                {tool.title}
              </h3>
              <p className="text-sm text-davis-slate leading-relaxed">
                {tool.description}
              </p>
              {tool.attribution && (
                <p className="text-[11px] text-davis-slate/70 mt-3 pt-2 border-t border-gray-100">
                  Data from{' '}
                  <a
                    href={tool.attribution.dataSourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-davis-blue hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tool.attribution.dataSource}
                  </a>
                  {' '}&middot; Original VBA model by {tool.attribution.modelCredit} —{' '}
                  <a
                    href={`mailto:${tool.attribution.email}`}
                    className="text-davis-blue hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tool.attribution.email}
                  </a>
                </p>
              )}
            </Link>
          ))}
        </div>

        {/* Contact card */}
        <a
          href="https://baycrewtech.com/contact"
          target="_blank"
          rel="noopener noreferrer"
          className="group block bg-gradient-to-br from-davis-navy to-davis-navy-light border border-davis-navy/20 rounded-xl p-6 hover:shadow-lg transition-all mt-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-davis-gold/15 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-davis-gold" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0l-9.75 6.093L2.25 6.75" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-display text-white group-hover:text-davis-gold transition-colors mb-1">
                Contact Us
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">
                Need a custom tool, data visualization, or web application for your community or business? Get in touch with Bay Crew Tech.
              </p>
            </div>
            <svg className="w-5 h-5 text-davis-gold/50 group-hover:text-davis-gold group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </div>
        </a>
      </div>
    </div>
  );
}
